/**
 * Minimal ERC20 helpers for USDC on Base. Covers the three calls the
 * shell actually makes: read balance, read allowance, request approval.
 * All amounts are 6-decimal integers (USDC convention).
 */

import { Contract, parseUnits, formatUnits, type ContractRunner, type TransactionResponse } from "ethers";

export const USDC_ABI = [
  "function balanceOf(address owner) view returns (uint256)",
  "function allowance(address owner, address spender) view returns (uint256)",
  "function approve(address spender, uint256 amount) returns (bool)",
  "function decimals() view returns (uint8)",
] as const;

function getUsdcAddress(): string {
  const addr = process.env.NEXT_PUBLIC_USDC_ADDRESS;
  if (!addr) throw new Error("NEXT_PUBLIC_USDC_ADDRESS is not set.");
  return addr;
}

export function usdcContract(runner: ContractRunner): Contract {
  return new Contract(getUsdcAddress(), USDC_ABI, runner);
}

/** Convert a human-readable dollar amount (e.g. 25) to USDC 6-decimal integer. */
export function toUsdc(dollars: number | string): bigint {
  return parseUnits(String(dollars), 6);
}

/** Convert USDC 6-decimal integer to a JS number in dollars (rounded to cents). */
export function fromUsdc(amount: bigint): number {
  // Render as "X.XX" then parse to keep display precision identical to
  // the formatter used in the UserCard.
  return Number(formatUnits(amount, 6));
}

export async function getUSDCBalance(runner: ContractRunner, owner: string): Promise<bigint> {
  const c = usdcContract(runner);
  return (await c.balanceOf(owner)) as bigint;
}

export async function checkAllowance(
  runner: ContractRunner,
  owner: string,
  spender: string,
): Promise<bigint> {
  const c = usdcContract(runner);
  return (await c.allowance(owner, spender)) as bigint;
}

export async function approveUSDC(
  runner: ContractRunner,
  spender: string,
  amount: bigint,
): Promise<TransactionResponse> {
  const c = usdcContract(runner);
  const tx = (await c.approve(spender, amount)) as TransactionResponse;
  return tx;
}
